import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import moment from 'moment';
import './InvoicesTable.scss';
import { AppButton } from '../../../general/AppButton/AppButton';
import {
  QuickbooksInvoice,
  InvoiceStatus,
  AppFile,
  Supplier,
  formatMoney,
  roundTo,
  QuickbooksCreditMemo,
  QuickbooksInvoiceSchema,
  QuickbooksCreditMemoSchema,
  DillInvoice,
} from '@dill/dill-shared';
import { getDocumentPdf, getInvoicePdf } from '../../../../redux/services/buyersService';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  resetInvoiceBase64,
  selectInvoiceIdsForDownload,
  unSelectInvoiceIdsForDownload,
} from '../../../../redux/globalSlices/buyersSlice';
import { logEvent } from 'firebase/analytics';
import { myAnalytics } from '../../../../firebase/config';
import {
  canBuyerPaySupplier,
  downloadFile,
  firebaseFileUploadHelper,
  openDillToast,
  printFile,
  truncateText,
} from '../../../../utils/helpers';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import { Eye, Download, MessageCircle } from 'react-feather';
import { AppPdfListModal } from '../../../general/AppPdfListModal/AppPdfListModal';
import {
  resetSelectCreditMemo,
  selectCreditMemo,
  selectInvoice,
  unSelectCreditMemo,
  unSelectInvoice,
} from '../../../../redux/globalSlices/invoiceSlice';
import { getJobs } from '../../../../redux/services/jobsService';
import AppViewInvoiceWithoutPdfModal from '../../../general/AppViewInvoiceWithoutPdfModal/AppViewInvoiceWithoutPdfModal';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { twMerge } from 'tailwind-merge';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { ChatCircleText, Printer, DotsThreeVertical } from 'phosphor-react';
// import { AppChatModal } from '../../../general/AppChatModal/AppChatModal';
import { sendMessagesInvoice } from '../../../../redux/services/invoicesService';
import AppNotesModal from '../../../general/AppNotesModal/AppNotesModal';
import { useWindowActualWidth } from '../../../../utils/domTools';
import { DillButton, DillCheckbox, DillDropdownButton, DillTable } from '../../../general/Dill';
import type { DillColumn } from '../../../general/Dill';
import { useBuyerUserPermissions } from '../../../../utils/hooks/useBuyerUserPermissions';

export const InvoicesTable = ({
  invoices,
  selectedTabId,
  onPaySingleInvoice,
  selectedInvoiceForPreview,
  selectedCreditMemoForPreview,
  accumulateAmount,
  selectedRowId,
  onRowClick,
}: {
  invoices: (QuickbooksInvoice | QuickbooksCreditMemo)[];
  selectedTabId: InvoiceStatus;
  selectedInvoiceForPreview: QuickbooksInvoice | null | undefined;
  selectedCreditMemoForPreview: QuickbooksCreditMemo | null | undefined;
  onPaySingleInvoice: (invoice: QuickbooksInvoice) => void;
  accumulateAmount: number;
  selectedRowId?: string | null;
  onRowClick?: (row: QuickbooksInvoice | QuickbooksCreditMemo) => void;
}) => {
  const { invoiceBase64, invoiceIdsForDownload, previewPdf } = useAppSelector(
    (state) => state.buyers
  );
  const { selectedBuyerSuppliers } = useAppSelector((state) => state.buyers);
  const { selectedCreditMemos, selectedSupplierForBuyer } = useAppSelector(
    (state) => state.invoice
  );
  const { user } = useAppSelector((state) => state.auth);
  const { scheduledPayments } = useAppSelector((state) => state.payments);
  const { buyerUserCanPay } = useBuyerUserPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const width = useWindowActualWidth();
  const smallDeviceWidth = 800;

  const [selectedMainBuyerId] = useParamMainBuyerId();

  const [currentSupplierDomainId, setCurrentSupplierDomainId] = useState('');
  const [openPdfPreviewModal, setOpenPdfPreviewModal] = useState(false);
  const [isViewInvoiceWithoutPdfModalOpen, setIsViewInvoiceWithoutPdfModalOpen] = useState(false);
  const [openPdfListModal, setOpenPdfListModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<QuickbooksInvoice | null>(null);
  const [selectedCreditMemo, setSelectedCreditMemo] = useState<QuickbooksCreditMemo | null>(null);
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  const [downloadPdfList, setDownloadPdfList] = useState([] as any);
  const [previewPdfList, setPreviewPdfList] = useState([] as any);
  const [invoiceSigned, setInvoiceSigned] = useState(false);
  const [openCustomDropdown, setOpenCustomDropDown] = useState('');
  const [openChatModal, setOpenChatModal] = useState(false);
  const [loadingNote, setLoadingNote] = useState(false);
  const [fileList, setFileList] = useState<File[]>([]);
  const [category, setCategory] = useState('');

  /**
   * Invoices already committed to an open scheduled payment, keyed by every id they might be
   * matched on. They're badged and made unselectable so a buyer can't queue a second payment for
   * the same invoice — the backend rejects that too, but failing at selection is clearer.
   */
  const scheduledDateByInvoiceId = useMemo(() => {
    const result = new Map<string, Date>();
    for (const scheduledPayment of scheduledPayments) {
      for (const invoicePayment of scheduledPayment.invoicePayments ?? []) {
        [invoicePayment.providerInvoiceId, invoicePayment.quickbooksInvoiceId]
          .filter((key): key is string => Boolean(key))
          .forEach((key) => result.set(key, new Date(scheduledPayment.scheduledDate)));
      }
    }
    return result;
  }, [scheduledPayments]);

  const getScheduledDateForRow = (row: any): Date | undefined =>
    scheduledDateByInvoiceId.get(row?.id ?? '') ??
    scheduledDateByInvoiceId.get(row?.platformInvoiceId ?? '');

  const isAnySignedInvoice = useMemo(() => {
    let check = false;
    if (invoices) {
      check = invoices.some((documentDetails) => {
        const invoiceCheck = QuickbooksInvoiceSchema.safeParse(documentDetails);
        const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(documentDetails);
        let invoice: QuickbooksInvoice | null = null;
        let creditMemo: QuickbooksCreditMemo | null = null;
        if (invoiceCheck.success) {
          invoice = invoiceCheck.data;
        }
        if (creditMemoCheck.success) {
          creditMemo = creditMemoCheck.data;
        }
        if (!creditMemo) {
          return false;
        }
        return invoice && invoice.dillInvoice?.receivedBy;
      });
    }
    return check;
  }, [invoices]);

  useEffect(() => {
    if (invoiceBase64) {
      window.open('data:application/pdf;base64, ' + invoiceBase64, '_blank');

      dispatch(resetInvoiceBase64());
    }
    return () => {};
  }, [invoiceBase64]);

  useEffect(() => {
    if (previewPdf && previewMode) {
      setInvoiceSigned(false);
      setPreviewPdfList([{ url: previewPdf, name: 'pdf', date: new Date() }]);
      setOpenPdfPreviewModal(true);
      logEvent(myAnalytics, 'buyer_preview_invoice', {
        userId: user ? user.id : '',
        time: new Date(),
        route: location.pathname,
      });
    }
  }, [previewPdf]);

  useEffect(() => {
    if (location.pathname) {
      if (params.supplierDomainId) {
        setCurrentSupplierDomainId(params.supplierDomainId);
      }
    }

    return () => {};
  }, [location.pathname]);

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(getJobs({ mainBuyerId: selectedMainBuyerId }));
    }

    return () => {};
  }, [selectedMainBuyerId]);
  const selectedSupplier = useMemo(() => {
    const newSupplier: Supplier | null = null;
    if (params.supplierDomainId) {
      const supplierDomainId = params.supplierDomainId;

      const supplier: Supplier | undefined = selectedBuyerSuppliers.find(
        (item: Supplier) => item.domainId === supplierDomainId
      );
      return supplier;
    }
    return newSupplier;
  }, [location.pathname, selectedBuyerSuppliers]);

  const handleDownloadPdf = async ({
    type,
    id,
    docNumber,
    signedPdfs,
  }: {
    type: 'INVOICE' | 'CREDIT_MEMO';
    id: string;
    docNumber: string;
    signedPdfs?: AppFile[] | undefined | null;
  }) => {
    setPreviewMode(false);
    setPrintMode(false);
    setSelectedInvoiceNumber(docNumber);
    if (signedPdfs && signedPdfs.length > 0) {
      const sortedPdfs = [...signedPdfs].sort((a, b) => {
        console.log(a, b);
        if (!b.date || !a.date) {
          return 0;
        }
        return new Date(b.date.toString()).getTime() > new Date(a.date.toString()).getTime()
          ? 1
          : -1;
      });
      if (sortedPdfs.length > 1) {
        setDownloadPdfList(sortedPdfs);
        setOpenPdfListModal(true);
      } else {
        downloadFile(sortedPdfs[0].url || '', `invoice-${docNumber}-${+new Date()}.pdf`);
        logEvent(myAnalytics, 'buyer_download_invoice', {
          userId: user ? user.id : '',
          time: new Date(),
          route: location.pathname,
        });
      }
    } else {
      const res = await dispatch(
        getDocumentPdf({
          supplierDomainId: currentSupplierDomainId,
          id,
          type,
        })
      );
      if (res && res.type === 'buyers/getDocumentPdf/fulfilled') {
        openDillToast({ message: 'invoice pdf successfully downloaded', type: 'SUCCESS' });
      } else {
        openDillToast({ message: 'Failed to download invoice pdf', type: 'ERROR' });
      }
      logEvent(myAnalytics, 'buyer_download_invoice', {
        userId: user ? user.id : '',
        time: new Date(),
        route: location.pathname,
      });
    }
  };

  const handlePrintPdf = async ({
    type,
    id,
    docNumber,
    signedPdfs,
  }: {
    type: 'INVOICE' | 'CREDIT_MEMO';
    id: string;
    docNumber: string;
    signedPdfs?: AppFile[] | undefined | null;
  }) => {
    setPreviewMode(false);
    setPrintMode(true);
    setSelectedInvoiceNumber(docNumber);
    if (signedPdfs && signedPdfs.length > 0) {
      const sortedPdfs = [...signedPdfs].sort((a, b) => {
        console.log(a, b);
        if (!b.date || !a.date) {
          return 0;
        }
        return new Date(b.date.toString()).getTime() > new Date(a.date.toString()).getTime()
          ? 1
          : -1;
      });
      if (sortedPdfs.length > 1) {
        setDownloadPdfList(sortedPdfs);
        setOpenPdfListModal(true);
      } else {
        printFile(sortedPdfs[0].url || '', `invoice-${docNumber}-${+new Date()}.pdf`);
        logEvent(myAnalytics, 'buyer_print_invoice', {
          userId: user ? user.id : '',
          time: new Date(),
          route: location.pathname,
        });
      }
    } else {
      const res = await dispatch(
        getDocumentPdf({
          supplierDomainId: currentSupplierDomainId,
          id,
          type,
          print: true,
        })
      );
      if (res && res.type !== 'buyers/getDocumentPdf/fulfilled') {
        openDillToast({ message: 'Error printing the invoice pdf', type: 'ERROR' });
      }
      logEvent(myAnalytics, 'buyer_print_invoice', {
        userId: user ? user.id : '',
        time: new Date(),
        route: location.pathname,
      });
    }
  };

  const handlePreviewPdf = async ({
    type,
    id,
    docNumber,
    signedPdfs,
  }: {
    type: 'INVOICE' | 'CREDIT_MEMO';
    id: string;
    docNumber: string;
    signedPdfs?: AppFile[] | undefined | null;
  }) => {
    if (type === 'INVOICE') {
      setSelectedInvoiceNumber(docNumber);
    }
    setPreviewMode(true);
    setPrintMode(false);
    if (signedPdfs && signedPdfs.length > 0) {
      const sortedPdfs = [...signedPdfs].sort((a, b) => {
        console.log(a, b);
        if (!b.date || !a.date) {
          return 0;
        }
        return new Date(b.date.toString()).getTime() > new Date(a.date.toString()).getTime()
          ? 1
          : -1;
      });
      setInvoiceSigned(true);
      setPreviewPdfList(sortedPdfs);
      setOpenPdfPreviewModal(true);
      logEvent(myAnalytics, 'buyer_preview_invoice', {
        userId: user ? user.id : '',
        time: new Date(),
        route: location.pathname,
      });
    } else {
      const res = await dispatch(
        getDocumentPdf({
          supplierDomainId: currentSupplierDomainId,
          id,
          type,
          preview: true,
        })
      );
      console.log({ res });

      if (res && res.type !== 'buyers/getDocumentPdf/fulfilled') {
        openDillToast({ message: 'Failed to download invoice pdf', type: 'ERROR' });
        if (selectedSupplier) {
          navigate(`/companies/${selectedMainBuyerId}/invoices/${selectedSupplier.domainId}`);
        }
      }
    }
  };

  const handlePreviewInvoiceWithoutPdf = (invoice: QuickbooksInvoice) => {
    setSelectedInvoice(invoice);
    setIsViewInvoiceWithoutPdfModalOpen(true);
  };

  const handleDownloadSelectedPdf = (url: string) => {
    if (printMode) {
      printFile(url, `invoice-${selectedInvoiceNumber}-${+new Date()}.pdf`);
    } else {
      downloadFile(url, `invoice-${selectedInvoiceNumber}-${+new Date()}.pdf`);
    }
  };

  const getDocumentType = (
    invoice: QuickbooksInvoice | null,
    creditMemo: QuickbooksCreditMemo | null
  ): string => {
    if (invoice && invoice.recordType === 'FINANCE_CHARGE') {
      return 'Finance Charge';
    } else if (invoice && invoice.recordType === 'DEBIT_MEMO') {
      return 'Debit Memo';
    } else if (invoice && invoice.recordType === 'PAYMENT') {
      return 'Payment';
    } else if (invoice && !invoice.recordType) {
      return 'Invoice';
    } else if (creditMemo && creditMemo.recordType === 'PAYMENT') {
      return 'Payment';
    } else if (creditMemo && creditMemo.recordType === 'MISC_CREDITS') {
      return 'Misc Credits';
    } else if (creditMemo && creditMemo.recordType === 'CREDIT_INVOICE') {
      return 'Credits Invoice';
    } else if (creditMemo) {
      return 'Credit Memo';
    } else {
      return '';
    }
  };

  const isCreditMemoSelectable = (
    creditMemo: QuickbooksCreditMemo,
    integrationType: string | undefined
  ) =>
    ((creditMemo.recordType === 'CREDIT_MEMO' || creditMemo.recordType === 'CREDIT_INVOICE') &&
      integrationType === 'P21') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'SAGE_100') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'MISC_CREDITS' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'INFOR_DIST_SX') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'DISTRIBUTION_ONE') ||
    (creditMemo.recordType === 'CREDIT_MEMO' && integrationType === 'MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' || creditMemo.recordType === 'CREDIT_INVOICE') &&
      integrationType === 'ABLE_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' || creditMemo.recordType === 'CREDIT_INVOICE') &&
      integrationType === 'P21_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'DDI_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'ECLIPSE_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'MARKS_MANUAL_UPLOAD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'RUTTER_QBD') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'RUTTER_NETSUITE') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'ECLIPSE') ||
    ((creditMemo.recordType === 'CREDIT_MEMO' ||
      creditMemo.recordType === 'CREDIT_INVOICE' ||
      creditMemo.recordType === 'PAYMENT') &&
      integrationType === 'EAGLE_MANUAL_UPLOAD') ||
    integrationType === 'QBO';
  const validInvoices = useMemo(
    () =>
      invoices.filter(
        (documentDetails) => QuickbooksInvoiceSchema.safeParse(documentDetails).success
      ),
    [invoices]
  );

  const dillTableSelectedRowIds = useMemo(() => {
    const invoiceIds = invoiceIdsForDownload[selectedTabId] || [];
    const creditMemoIds = (selectedCreditMemos[selectedTabId] || []).map((cm) => cm.id);
    return new Set<string>([...invoiceIds, ...creditMemoIds]);
  }, [invoiceIdsForDownload, selectedTabId, selectedCreditMemos]);

  const handleDillSelectionChange = (rows: (QuickbooksInvoice | QuickbooksCreditMemo)[]) => {
    const integrationType = selectedSupplierForBuyer?.integrationType ?? undefined;
    const prevInvoiceIds = invoiceIdsForDownload[selectedTabId] || [];
    const prevCreditMemoIds = (selectedCreditMemos[selectedTabId] || []).map((cm) => cm.id);

    const newInvoices = rows
      .filter((r) => QuickbooksInvoiceSchema.safeParse(r).success)
      .map((r) => QuickbooksInvoiceSchema.parse(r));
    const newCreditMemos = rows
      .filter((r) => {
        const check = QuickbooksCreditMemoSchema.safeParse(r);
        return check.success && isCreditMemoSelectable(check.data, integrationType);
      })
      .map((r) => QuickbooksCreditMemoSchema.parse(r));

    const newInvoiceIds = newInvoices.map((r) => r.id);
    const newCreditMemoIds = newCreditMemos.map((cm) => cm.id);

    const addedInvoices = newInvoices.filter((inv) => !prevInvoiceIds.includes(inv.id));
    const removedInvoiceIds = prevInvoiceIds.filter((id) => !newInvoiceIds.includes(id));
    const addedCreditMemos = newCreditMemos.filter((cm) => !prevCreditMemoIds.includes(cm.id));
    const removedCreditMemoIds = prevCreditMemoIds.filter((id) => !newCreditMemoIds.includes(id));

    if (addedInvoices.length > 0) {
      dispatch(
        selectInvoiceIdsForDownload({
          tabId: selectedTabId,
          invoiceIds: addedInvoices.map((inv) => inv.id),
        })
      );
      if (selectedTabId !== 'PAID' && selectedTabId !== 'PENDING_PAY') {
        dispatch(selectInvoice({ tabId: selectedTabId, invoices: addedInvoices }));
      }
    }

    if (removedInvoiceIds.length > 0) {
      dispatch(
        unSelectInvoiceIdsForDownload({ tabId: selectedTabId, invoiceIds: removedInvoiceIds })
      );
      if (selectedTabId !== 'PAID' && selectedTabId !== 'PENDING_PAY') {
        const removedInvoices = invoices
          .filter(
            (inv) =>
              QuickbooksInvoiceSchema.safeParse(inv).success && removedInvoiceIds.includes(inv.id)
          )
          .map((inv) => QuickbooksInvoiceSchema.parse(inv));
        if (removedInvoices.length > 0) {
          dispatch(unSelectInvoice({ tabId: selectedTabId, invoices: removedInvoices }));
        }
      }
    }

    if (
      addedCreditMemos.length > 0 &&
      selectedTabId !== 'PAID' &&
      selectedTabId !== 'PENDING_PAY'
    ) {
      dispatch(selectCreditMemo({ tabId: selectedTabId, creditMemos: addedCreditMemos }));
    }

    if (
      removedCreditMemoIds.length > 0 &&
      selectedTabId !== 'PAID' &&
      selectedTabId !== 'PENDING_PAY'
    ) {
      const removedCreditMemos = invoices
        .filter(
          (cm) =>
            QuickbooksCreditMemoSchema.safeParse(cm).success && removedCreditMemoIds.includes(cm.id)
        )
        .map((cm) => QuickbooksCreditMemoSchema.parse(cm));
      if (removedCreditMemos.length > 0) {
        dispatch(unSelectCreditMemo({ tabId: selectedTabId, creditMemos: removedCreditMemos }));
      }
    }
  };

  const dillColumns = useMemo<DillColumn<QuickbooksInvoice | QuickbooksCreditMemo>[]>(() => {
    const NET_TERMS_REGEX = /^(\d+%\s?.*|.*\s?\d+%)$/;
    const CVS_PLUMBING_DISCOUNT_TERMS = ['210N25', '210N30', '210N30X15'];
    const DISCOUNT_INTEGRATIONS = [
      'MANUAL_UPLOAD',
      'DDI_MANUAL_UPLOAD',
      'P21_MANUAL_UPLOAD',
      'SAGE_100',
      'DISTRIBUTION_ONE',
      'RUTTER_QBD',
      'ECLIPSE',
      'ECLIPSE_MANUAL_UPLOAD',
      'EAGLE_MANUAL_UPLOAD',
    ];

    const cols: DillColumn<any>[] = [
      // ── Document # ──────────────────────────────────────────────────────────
      {
        key: 'docNumber',
        label: <span className="text-nowrap">Document #</span>,
        width: selectedTabId === 'PENDING_PAY' ? 120 : 90,
        sortable: true,
        getSortValue: (row) => row.docNumber ?? '',
        render: (_, row) => {
          const scheduledDate = getScheduledDateForRow(row);
          return (
            <div className="flex flex-col">
              <span className="text-xs TEXT_PRIMARY-CLR">{row.docNumber}</span>
              {scheduledDate && (
                <span className="text-2xs font-semibold PRIMARY_500-CLR text-nowrap mt-0.5">
                  Scheduled {moment(scheduledDate).format('MM/DD')}
                </span>
              )}
            </div>
          );
        },
      },

      // ── Type ────────────────────────────────────────────────────────────────
      {
        key: 'type',
        label: 'Type',
        width: 80,
        sortable: true,
        getSortValue: (row) => {
          const inv = QuickbooksInvoiceSchema.safeParse(row).data ?? null;
          const cm = QuickbooksCreditMemoSchema.safeParse(row).data ?? null;
          return getDocumentType(inv, cm);
        },
        render: (_, row) => {
          const inv = QuickbooksInvoiceSchema.safeParse(row).data ?? null;
          const cm = QuickbooksCreditMemoSchema.safeParse(row).data ?? null;
          return <span className="text-xs TEXT_PRIMARY-CLR">{getDocumentType(inv, cm)}</span>;
        },
      },

      // ── PO # ────────────────────────────────────────────────────────────────
      {
        key: 'poNumber',
        label: 'PO #',
        width: 80,
        sortable: true,
        getSortValue: (row) => row.poNumber ?? '',
        render: (_, row) => (
          <div className="flex">
            <p
              data-tooltip-id={'buyer-invoice-po-number-' + row.id}
              className="text-xs TEXT_PRIMARY-CLR break-words line-clamp-4">
              {row.poNumber}
            </p>
            <ReactTooltip
              id={'buyer-invoice-po-number-' + row.id}
              place="right"
              variant="dark"
              opacity="100%"
              style={{ background: '#222', width: '200px', zIndex: 99999 }}>
              <span>{row.poNumber}</span>
            </ReactTooltip>
          </div>
        ),
      },

      // ── Job ─────────────────────────────────────────────────────────────────
      {
        key: 'job',
        label: 'Job',
        width: selectedTabId === 'PAID' ? 100 : 150,
        sortable: true,
        getSortValue: (row) => row.quickBooksJob?.jobName ?? '',
        render: (_, row) => {
          if (!row.quickBooksJob) return null;
          const jobName = `${
            row.quickBooksJob.jobNumber ? `${row.quickBooksJob.jobNumber} - ` : ''
          }${row.quickBooksJob.jobName ?? ''}`;
          const jobAddress = `${row.quickBooksJob.address || row.shipAddress?.address1 || ''} ${
            row.quickBooksJob.city || ''
          } ${row.quickBooksJob.state || ''}`.trim();
          if (!jobName && !jobAddress) return null;
          return (
            <div className="text-xs w-full px-0.5">
              <p className="font-bold text-xs truncate">{jobName}</p>
              {jobAddress && (
                <>
                  <p
                    data-tooltip-id={'buyer-invoice-job-address-' + row.id}
                    className="text-2xs line-clamp-2 cursor-default">
                    {jobAddress}
                  </p>
                  <ReactTooltip
                    id={'buyer-invoice-job-address-' + row.id}
                    place="top"
                    variant="dark"
                    opacity="100%"
                    style={{ background: '#222', width: '240px', zIndex: 99999 }}>
                    <span>{jobAddress}</span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
      },

      // ── Due Date ────────────────────────────────────────────────────────────
      {
        key: 'dueDate',
        label: 'Due Date',
        width: selectedTabId === 'PAID' ? 140 : 120,
        sortable: true,
        getSortValue: (row) => row.dueDate ?? '',
        render: (_, row) => {
          const dueDateValue = row.dueDate ? moment(row.dueDate).utc().format('MM/DD/YYYY') : '';
          const isPastDue =
            selectedTabId === 'OPEN' &&
            QuickbooksInvoiceSchema.safeParse(row).data?.status === 'PAST_DUE';
          return (
            <span className={twMerge('text-xs', isPastDue ? 'ERROR_500-CLR' : 'TEXT_PRIMARY-CLR')}>
              {dueDateValue}
            </span>
          );
        },
      },

      // ── Date (createdAt) ─────────────────────────────────────────────────────
      {
        key: 'createdAt',
        label: 'Date',
        width: selectedTabId === 'PAID' ? 140 : 120,
        sortable: true,
        getSortValue: (row) => row.createdAt?.toString() ?? '',
        render: (_, row) => {
          const dateValue = row.createdAt ? moment(row.createdAt).utc().format('MM/DD/YYYY') : '';
          return <span className="text-xs TEXT_PRIMARY-CLR">{dateValue}</span>;
        },
      },

      // ── Terms ────────────────────────────────────────────────────────────────
      {
        key: 'terms',
        label: 'Terms',
        width: selectedTabId === 'PAID' ? 140 : 80,
        sortable: true,
        getSortValue: (row) => row.terms ?? '',
        render: (_, row) => {
          return <span className="text-xs TEXT_PRIMARY-CLR">{row.terms || row.discountTerms}</span>;
        },
      },

      // ── Amount ───────────────────────────────────────────────────────────────
      {
        key: 'amount',
        label: 'Amount',
        width: selectedTabId === 'PAID' ? 140 : 120,
        sortable: true,
        numeric: true,
        getSortValue: (row) => {
          const inv = QuickbooksInvoiceSchema.safeParse(row);
          const cm = QuickbooksCreditMemoSchema.safeParse(row);
          if (inv.success) return parseFloat(String(inv.data.amount ?? '0'));
          if (cm.success) {
            const total = parseFloat(String(Math.abs(cm.data.totalAmount) ?? '0'));
            if (
              cm.data.recordType === 'CREDIT_MEMO' ||
              cm.data.recordType === 'PAYMENT' ||
              cm.data.recordType === 'MISC_CREDITS' ||
              cm.data.recordType === 'CREDIT_INVOICE'
            ) {
              return -total;
            }
            return total;
          }
          return 0;
        },
        render: (_, row) => {
          const inv = QuickbooksInvoiceSchema.safeParse(row).data ?? null;
          const cm = QuickbooksCreditMemoSchema.safeParse(row).data ?? null;
          let prefix = '';
          if (
            cm &&
            (cm.recordType === 'CREDIT_MEMO' ||
              cm.recordType === 'PAYMENT' ||
              cm.recordType === 'MISC_CREDITS' ||
              cm.recordType === 'CREDIT_INVOICE')
          ) {
            prefix = '-';
          }
          const amountValue = inv
            ? `${prefix}$${roundTo(parseFloat((row as any).amount || '0'), 2)}`
            : cm
            ? `${prefix}$${roundTo(parseFloat(Math.abs(cm.totalAmount).toString() || '0'), 2)}`
            : '';
          const isCvsPlumbingDiscountTerm =
            selectedSupplier?.domainId === 'cvsplumbing' &&
            CVS_PLUMBING_DISCOUNT_TERMS.includes(inv?.terms ?? '');
          const isMarksManualUploadDiscount =
            selectedSupplierForBuyer?.integrationType === 'MARKS_MANUAL_UPLOAD' &&
            !!inv?.invoiceDiscountAmount &&
            inv.invoiceDiscountAmount > 0 &&
            inv?.invoiceDiscountDate != null &&
            moment().isSameOrBefore(moment(inv.invoiceDiscountDate).endOf('day'));
          const discountAvailable =
            !!inv &&
            (((NET_TERMS_REGEX.test(inv.terms ?? '') || isCvsPlumbingDiscountTerm) &&
              DISCOUNT_INTEGRATIONS.includes(selectedSupplierForBuyer?.integrationType ?? '')) ||
              isMarksManualUploadDiscount);
          let discountTooltipText =
            'Click Pay, and any discounts will get automatically applied for you when you select your payment method.';
          if (isMarksManualUploadDiscount && inv?.invoiceDiscountAmount) {
            const discountDate = moment(inv.invoiceDiscountDate).format('MM/DD/YYYY');
            discountTooltipText = `Discount available: ${formatMoney(
              inv.invoiceDiscountAmount,
              2
            )} if paid by ${discountDate}`;
          }
          return (
            <div className="flex items-center justify-end pr-2 flex-col w-full">
              <p className="text-xs TEXT_PRIMARY-CLR text-right w-full">{amountValue}</p>
              {discountAvailable && (
                <>
                  <p
                    data-tooltip-id={'possible-discount-' + row.id}
                    className="text-[9px] text-right PRIMARY_500-CLR w-full cursor-default">
                    Possible Discount
                  </p>
                  <ReactTooltip
                    id={'possible-discount-' + row.id}
                    place="top"
                    variant="dark"
                    opacity="100%"
                    style={{ background: '#222', width: '240px', zIndex: 99999, fontSize: '9px' }}>
                    <span>{discountTooltipText}</span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
      },
    ];

    // ── Balance (not in PAID) ────────────────────────────────────────────────
    if (selectedTabId !== 'PAID') {
      cols.push({
        key: 'balance',
        label: 'Open',
        width: 120,
        sortable: true,
        numeric: true,
        getSortValue: (row) => {
          const cm = QuickbooksCreditMemoSchema.safeParse(row).data ?? null;
          const balance = parseFloat(String(row.balance ?? '0'));
          return cm ? -balance : balance;
        },
        render: (_, row) => {
          const cm = QuickbooksCreditMemoSchema.safeParse(row).data ?? null;
          let prefix = '';
          if (cm) prefix = '-';
          const balanceValue = `${prefix}$${roundTo(
            parseFloat(row.balance?.toString() || '0'),
            2
          )}`;
          return <p className="text-xs TEXT_PRIMARY-CLR text-right w-full pr-2">{balanceValue}</p>;
        },
      });
    }

    // ── Payment (PENDING_PAY only) ───────────────────────────────────────────
    if (selectedTabId === 'PENDING_PAY') {
      cols.push({
        key: 'payment',
        label: 'Payment',
        width: 120,
        sortable: true,
        numeric: true,
        getSortValue: (row) => {
          const inv = QuickbooksInvoiceSchema.safeParse(row).data;
          if (!inv) return 0;
          return (
            (inv.dillInvoicePaymentSummary?.amountPaid ?? 0) +
            (inv.dillInvoicePaymentSummary?.discountAmount ?? 0)
          );
        },
        render: (_, row) => {
          const inv = QuickbooksInvoiceSchema.safeParse(row).data;
          if (!inv) return null;
          const amountPaid =
            (inv.dillInvoicePaymentSummary?.amountPaid ?? 0) +
            (inv.dillInvoicePaymentSummary?.discountAmount ?? 0);
          return (
            <p className="text-xs TEXT_PRIMARY-CLR text-right w-full pr-2">
              {formatMoney(amountPaid || '0', 2)}
            </p>
          );
        },
      });
    }

    // ── Signed By (conditional) ──────────────────────────────────────────────
    if (isAnySignedInvoice) {
      cols.push({
        key: 'signedBy',
        label: 'Signed By',
        width: 250,
        render: (_, row) => {
          const inv = QuickbooksInvoiceSchema.safeParse(row).data;
          const dillInvoice = inv?.dillInvoice;
          if (!dillInvoice || !dillInvoice.isReceived)
            return <p className="text-xs text-center TEXT_PRIMARY-CLR">-</p>;
          return (
            <div className="flex flex-col items-center">
              {dillInvoice.receivedBy && (
                <p className="text-xs font-bold text-center TEXT_PRIMARY-CLR">
                  {dillInvoice.receivedBy}
                </p>
              )}
              {dillInvoice.receivedByTime && (
                <p className="text-xs text-center TEXT_PRIMARY-CLR">
                  {moment(dillInvoice.receivedByTime).format('MM/DD  LT')}
                </p>
              )}
            </div>
          );
        },
      });
    }

    // ── Actions ──────────────────────────────────────────────────────────────
    cols.push({
      key: 'actions',
      label: '',
      width: 140,
      sortable: false,
      render: (_, row) => {
        const inv = QuickbooksInvoiceSchema.safeParse(row).data ?? null;
        const cm = QuickbooksCreditMemoSchema.safeParse(row).data ?? null;
        const isPdfAvailable = !!(inv?.isPdfAvailable || cm?.isPdfAvailable);
        // Set when an open scheduled payment already covers this invoice.
        const payScheduledDate = getScheduledDateForRow(row);

        const options: { value: string; label: string; icon: any }[] = [];
        if (inv)
          options.push({
            value: 'sendMessage',
            label: 'Send Message',
            icon: <ChatCircleText size={16} />,
          });
        if (isPdfAvailable)
          options.push({ value: 'print', label: 'Print', icon: <Printer size={16} /> });
        if (isPdfAvailable && width <= 1480)
          options.push({ value: 'download', label: 'Download', icon: <Download size={16} /> });
        if (isPdfAvailable && width <= 1380)
          options.push({ value: 'preview', label: 'Preview', icon: <Eye size={16} /> });

        return (
          <div className="flex w-full items-center justify-end gap-2">
            {isPdfAvailable && (
              <DillDropdownButton
                variant="icon-default"
                options={options}
                onSelect={(selectVal) => {
                  const val = selectVal.value;
                  if (inv) {
                    setSelectedInvoice(inv);
                    if (val === 'print')
                      handlePrintPdf({
                        type: 'INVOICE',
                        id: inv.id,
                        docNumber: inv.docNumber,
                        signedPdfs: inv.dillInvoice?.signedPdfs,
                      });
                    if (val === 'download')
                      handleDownloadPdf({
                        type: 'INVOICE',
                        id: inv.id,
                        docNumber: inv.docNumber,
                        signedPdfs: inv.dillInvoice?.signedPdfs,
                      });
                    if (val === 'preview' && selectedSupplier) {
                      navigate(
                        `/companies/${selectedMainBuyerId}/invoices/${selectedSupplier.domainId}?invoiceId=${inv.id}`
                      );
                      if (inv.isPdfAvailable)
                        handlePreviewPdf({
                          id: inv.id,
                          type: 'INVOICE',
                          docNumber: inv.docNumber,
                          signedPdfs: inv.dillInvoice?.signedPdfs,
                        });
                      else handlePreviewInvoiceWithoutPdf(inv);
                    }
                  }
                  if (cm) {
                    setSelectedCreditMemo(cm);
                    if (val === 'print')
                      handlePrintPdf({ type: 'CREDIT_MEMO', id: cm.id, docNumber: cm.docNumber });
                    if (val === 'download')
                      handleDownloadPdf({
                        id: cm.id,
                        docNumber: cm.docNumber,
                        type: 'CREDIT_MEMO',
                      });
                    if (val === 'preview' && selectedSupplier) {
                      navigate(
                        `/companies/${selectedMainBuyerId}/invoices/${selectedSupplier.domainId}?creditMemoId=${cm.id}`
                      );
                      if (cm.isPdfAvailable)
                        handlePreviewPdf({
                          id: cm.id,
                          type: 'CREDIT_MEMO',
                          docNumber: cm.docNumber,
                        });
                    }
                  }
                  if (val === 'sendMessage') setOpenChatModal(true);
                }}
                icon={<DotsThreeVertical size={18} />}
                height={32}
                width={32}
              />
            )}
            {isPdfAvailable && width > 1380 && (
              <DillButton
                variant="icon-default"
                height={32}
                width={32}
                icon={<Eye size={18} />}
                onClick={() => {
                  if (inv && selectedSupplier) {
                    setSelectedInvoice(inv);
                    navigate(
                      `/companies/${selectedMainBuyerId}/invoices/${selectedSupplier.domainId}?invoiceId=${inv.id}`
                    );
                    if (inv.isPdfAvailable)
                      handlePreviewPdf({
                        id: inv.id,
                        type: 'INVOICE',
                        docNumber: inv.docNumber,
                        signedPdfs: inv.dillInvoice?.signedPdfs,
                      });
                    else handlePreviewInvoiceWithoutPdf(inv);
                  }
                  if (cm && selectedSupplier) {
                    setSelectedCreditMemo(cm);
                    navigate(
                      `/companies/${selectedMainBuyerId}/invoices/${selectedSupplier.domainId}?creditMemoId=${cm.id}`
                    );
                    if (cm.isPdfAvailable)
                      handlePreviewPdf({ id: cm.id, type: 'CREDIT_MEMO', docNumber: cm.docNumber });
                  }
                }}
              />
            )}
            {isPdfAvailable && width > 1880 && (
              <DillButton
                variant="icon-default"
                icon={<Download size={18} />}
                onClick={() => {
                  if (inv)
                    handleDownloadPdf({
                      id: inv.id,
                      type: 'INVOICE',
                      docNumber: inv.docNumber,
                      signedPdfs: inv.dillInvoice?.signedPdfs,
                    });
                  if (cm)
                    handleDownloadPdf({ id: cm.id, docNumber: cm.docNumber, type: 'CREDIT_MEMO' });
                }}
              />
            )}
            <div
              data-tooltip-id={
                // eslint-disable-next-line no-nested-ternary
                !buyerUserCanPay
                  ? 'no_pay_permission'
                  : payScheduledDate
                  ? 'invoice-already-scheduled-' + row.id
                  : 'supplier-is-verified-' + row.id
              }>
              {inv && selectedTabId !== 'PAID' && selectedTabId !== 'PENDING_PAY' ? (
                <DillButton
                  label="Pay"
                  onClick={() => inv && onPaySingleInvoice(inv)}
                  height={32}
                  isDisabled={
                    !!(selectedSupplier && !canBuyerPaySupplier({ supplier: selectedSupplier })) ||
                    !inv ||
                    !buyerUserCanPay ||
                    // Already committed to a scheduled payment. Paying now would double-pay it —
                    // the backend rejects it too, but the button should not invite the attempt.
                    Boolean(payScheduledDate)
                  }
                />
              ) : (
                <div className="h-[36px] w-[50px]" />
              )}
            </div>
            {payScheduledDate && (
              <ReactTooltip
                id={'invoice-already-scheduled-' + row.id}
                place="right"
                variant="dark"
                opacity="100%"
                style={{ background: '#222', width: '300px', zIndex: 99999 }}>
                <span>
                  {`A payment for this invoice is already scheduled for ${moment(
                    payScheduledDate
                  ).format('MM/DD/YYYY')}. Cancel it from the Payments page to pay now.`}
                </span>
              </ReactTooltip>
            )}
            {selectedSupplier && !canBuyerPaySupplier({ supplier: selectedSupplier }) && (
              <ReactTooltip
                id={'supplier-is-verified-' + row.id}
                place="right"
                variant="dark"
                opacity="100%"
                style={{ background: '#222', width: '300px', zIndex: 99999 }}>
                <span>Supplier verification in progress. Check back in a business day or two</span>
              </ReactTooltip>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [
    selectedTabId,
    isAnySignedInvoice,
    invoiceIdsForDownload,
    selectedCreditMemos,
    selectedSupplierForBuyer?.integrationType,
    invoices,
    width,
    selectedSupplier,
    selectedMainBuyerId,
    dispatch,
  ]);

  const handleSendMessage = async (message: string) => {
    try {
      if (selectedInvoice) {
        setLoadingNote(true);
        const attachedFiles: { name: string; url: string; fileType: string }[] = [];
        for (let index = 0; index < fileList.length; index++) {
          const selectedFile = fileList[index];
          const blobUrl = URL.createObjectURL(selectedFile);
          const fileName = `${new Date().getTime()}-${selectedFile.name}`;
          const downloadUrl = await firebaseFileUploadHelper({
            blobUrl: blobUrl,
            fileNameWithExtension: `${fileName}`,
            storagePath: `invoices/sendMessage/attachments/${selectedMainBuyerId}/${selectedInvoice.id}`,
          });
          attachedFiles.push({
            url: downloadUrl ?? '',
            name: `${fileName}`,
            fileType: selectedFile.type,
          });
        }
        const result = await dispatch(
          sendMessagesInvoice({
            mainBuyerId: selectedMainBuyerId || '',
            invoiceId: selectedInvoice.id,
            data: {
              message,
              date: new Date(),
              supplierId: selectedSupplier?.id ?? '',
              buyerId: selectedMainBuyerId,
              recepientId: selectedMainBuyerId,
              isRead: false,
              attachements: attachedFiles,
              category,
            },
          })
        );
        setLoadingNote(false);
        if (result.type === 'suppliers/sendMessagesInvoice/fulfilled') {
          setOpenChatModal(false);
          openDillToast({ message: 'Successfully sent the message.', type: 'SUCCESS' });
        } else {
          openDillToast({ message: 'Failed to send the message.', type: 'ERROR' });
        }
      }
    } catch (err: any) {
      openDillToast({ message: err.message || 'Failed to send the message.', type: 'ERROR' });
      console.log(err);
    }
  };

  useEffect(() => {
    if (selectedSupplier && selectedInvoiceForPreview) {
      const invoice = selectedInvoiceForPreview;
      if (invoice) {
        if (invoice?.isPdfAvailable) {
          setSelectedInvoice(invoice);
          handlePreviewPdf({
            id: invoice.id,
            type: 'INVOICE',
            docNumber: invoice.docNumber,
            signedPdfs: invoice.dillInvoice?.signedPdfs,
          });
        } else {
          handlePreviewInvoiceWithoutPdf(selectedInvoiceForPreview);
        }
      }
    }
  }, [selectedInvoiceForPreview, selectedSupplier]);
  useEffect(() => {
    if (selectedSupplier && selectedCreditMemoForPreview) {
      const creditMemo = selectedCreditMemoForPreview;

      if (creditMemo) {
        if (creditMemo?.isPdfAvailable) {
          setSelectedCreditMemo(creditMemo);
          handlePreviewPdf({
            id: creditMemo.id,
            type: 'CREDIT_MEMO',
            docNumber: creditMemo.docNumber,
          });
        }
      }
    }
  }, [selectedCreditMemoForPreview, selectedSupplier]);

  return (
    <div className="h-full flex flex-col min-h-0">
      <AppNotesModal
        open={openChatModal}
        handleClose={() => setOpenChatModal(false)}
        onSubmit={(value: string) => handleSendMessage(value)}
        title={'Send Message'}
        primaryButtonText={'Send'}
        loading={loadingNote}
        hasAttachment={true}
        onSelectFile={(data) => setFileList(data)}
        hasDropDown={true}
        dropDownOptions={{
          label: 'Category',
          options: [
            { label: 'Dispute', value: 'Dispute' },
            { label: 'Other', value: 'Other' },
          ],
        }}
        onSelectDropDown={(data) => setCategory(data)}
      />
      {isViewInvoiceWithoutPdfModalOpen && (
        <AppViewInvoiceWithoutPdfModal
          open={isViewInvoiceWithoutPdfModalOpen}
          handleClose={() => {
            setIsViewInvoiceWithoutPdfModalOpen(false);
            navigate(location.pathname);
          }}
          selectedInvoice={selectedInvoice}
          selectedSupplier={selectedSupplier}
        />
      )}
      {width <= smallDeviceWidth && (
        <div className="flex px-4 py-2 items-center bg-white border-x border-t border-gray-200 sticky top-0 z-10">
          <DillCheckbox
            isChecked={
              invoiceIdsForDownload[selectedTabId] &&
              invoiceIdsForDownload[selectedTabId].length > 0
                ? invoiceIdsForDownload[selectedTabId].length === validInvoices.length
                : false
            }
            onChange={(isChecked: boolean) => {
              if (isChecked) {
                dispatch(
                  selectInvoiceIdsForDownload({
                    tabId: selectedTabId,
                    invoiceIds: invoices
                      .filter((documentDetails) => {
                        const invoiceCheck = QuickbooksInvoiceSchema.safeParse(documentDetails);
                        if (invoiceCheck.success) {
                          return true;
                        }
                        return false;
                      })
                      .map((invoice) => invoice.id),
                  })
                );
                if (selectedTabId !== 'PAID' && selectedTabId !== 'PENDING_PAY') {
                  dispatch(
                    selectInvoice({
                      tabId: selectedTabId,
                      invoices: invoices
                        .filter((documentDetails) => {
                          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(documentDetails);
                          if (invoiceCheck.success) {
                            return true;
                          }
                          return false;
                        })
                        .map((invoice) => QuickbooksInvoiceSchema.parse(invoice)),
                    })
                  );
                }
              } else {
                dispatch(
                  unSelectInvoiceIdsForDownload({
                    tabId: selectedTabId,
                    invoiceIds: invoices
                      .filter((documentDetails) => {
                        const invoiceCheck = QuickbooksInvoiceSchema.safeParse(documentDetails);
                        if (invoiceCheck.success) {
                          return true;
                        }
                        return false;
                      })
                      .map((invoice) => invoice.id),
                  })
                );
                if (selectedTabId !== 'PAID' && selectedTabId !== 'PENDING_PAY') {
                  dispatch(
                    unSelectInvoice({
                      tabId: selectedTabId,
                      invoices: invoices
                        .filter((documentDetails) => {
                          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(documentDetails);
                          if (invoiceCheck.success) {
                            return true;
                          }
                          return false;
                        })
                        .map((invoice) => QuickbooksInvoiceSchema.parse(invoice)),
                    })
                  );
                }
              }
            }}
          />
          <p className="text-sm font-medium  ml-2">Select all</p>
        </div>
      )}
      <DillTable
        columns={dillColumns}
        data={invoices}
        rowKey={(row) => row.id}
        sortCycle="3state"
        scrollX
        emptyMessage="No invoices"
        selectable
        search={false}
        striped={false}
        selectedRowIds={dillTableSelectedRowIds}
        onSelectionChange={handleDillSelectionChange}
        onRowClick={onRowClick}
        isRowSelectable={(row) => {
          if (getScheduledDateForRow(row)) return false;
          const invoice = QuickbooksInvoiceSchema.safeParse(row);
          if (invoice.success) {
            const isPendingReconciliation =
              invoice.data.dillPaymentStatus === 'RECONCILIATION_PENDING';
            const hasNegativeBalance = (invoice.data.balance ?? 0) < 0;
            if (isPendingReconciliation && hasNegativeBalance) return false;
            return true;
          }
          const creditMemo = QuickbooksCreditMemoSchema.safeParse(row);
          if (!creditMemo.success) return false;
          return isCreditMemoSelectable(
            creditMemo.data,
            selectedSupplierForBuyer?.integrationType ?? undefined
          );
        }}
        getRowDisabledReason={(row) => {
          const scheduledDate = getScheduledDateForRow(row);
          if (scheduledDate) {
            return `This invoice already has a payment scheduled for ${moment(scheduledDate).format(
              'MM/DD/YYYY'
            )}. Cancel it from the Payments page to select this invoice again.`;
          }
          const invoice = QuickbooksInvoiceSchema.safeParse(row);
          if (invoice.success) {
            const isPendingReconciliation =
              invoice.data.dillPaymentStatus === 'RECONCILIATION_PENDING';
            const hasNegativeBalance = (invoice.data.balance ?? 0) < 0;
            if (isPendingReconciliation && hasNegativeBalance) {
              return 'This invoice is pending reconciliation with a negative balance and cannot be selected.';
            }
          }
          return undefined;
        }}
        rowHeight={64}
        tableClassName="rounded-t-[0px] rounded-b-xl overflow-hidden"
      />

      <AppPdfListModal
        open={openPdfListModal}
        handleClose={() => {
          setOpenPdfListModal(false);
        }}
        pdfList={downloadPdfList}
        invoice={selectedInvoiceNumber}
        handleDownloadPdf={handleDownloadSelectedPdf}
        buttonText={printMode ? 'Print' : 'Download'}
      />
      <AppPdfPreviewModal
        open={openPdfPreviewModal}
        mode="Buyer"
        handleClose={() => {
          setOpenPdfPreviewModal(false);
          navigate(location.pathname);
          setSelectedCreditMemo(null);
          setSelectedInvoice(null);
        }}
        invoice={
          selectedInvoice
            ? selectedInvoice?.docNumber || ''
            : selectedCreditMemo
            ? selectedCreditMemo?.docNumber || ''
            : ''
        }
        selectedItem={selectedInvoice || selectedCreditMemo || null}
        pdfType={selectedInvoice ? 'INVOICE' : selectedCreditMemo ? 'CREDIT_MEMO' : undefined}
        pdfList={previewPdfList}
        handleDownloadPdf={handleDownloadSelectedPdf}
        isSigned={invoiceSigned}
        supplierDomainId={currentSupplierDomainId}
        onPay={
          // Withheld when an open scheduled payment already covers this invoice, matching the
          // disabled Pay button in the row actions.
          selectedInvoice && !getScheduledDateForRow(selectedInvoice)
            ? () => {
                onPaySingleInvoice(selectedInvoice);
              }
            : undefined
        }
      />
      <ReactTooltip
        id={'no_pay_permission'}
        place="bottom"
        variant="dark"
        opacity={'100%'}
        style={{
          background: '#222',
          width: '300px',
          zIndex: 99999,
        }}>
        <span>You don't have permissions to make payments. Please contact your account admin.</span>
      </ReactTooltip>
    </div>
  );
};
